import isEmpty from 'lodash/isEmpty'
import { useCallback } from 'react'
import type { File } from '@ztechapps/common.contract'
import { BusyButtonLink, type BusyButtonProps } from '@ztechapps/webui.button'
import { FileUploader, type OnUploadCompleteFunction, type UppyRestrictions } from '@ztechapps/webui.input'

/**
 * Supported MIME types for document/image analysis.
 */
export type SupportedMimeType = 'application/pdf' | 'image/png' | 'image/jpeg' | 'image/gif' | 'image/webp'

/**
 * Accepted file types for the file input.
 */
const ACCEPTED_FILE_TYPES = '.pdf,.png,.jpg,.jpeg,.gif,.webp,application/pdf,image/png,image/jpeg,image/gif,image/webp'

/**
 * File restrictions for uploads.
 */
const FILE_RESTRICTIONS: UppyRestrictions = {
  maxFileSize: 10 * 1024 * 1024, // 10MB
  maxNumberOfFiles: 1,
  allowedFileTypes: [
    '.pdf',
    'application/pdf',
    '.png',
    'image/png',
    '.jpg',
    '.jpeg',
    'image/jpeg',
    '.gif',
    'image/gif',
    '.webp',
    'image/webp',
  ],
}

export type AttachFileButtonProps = {
  /** Callback when a file is successfully attached */
  onFileAttached?: (file: File, mimeType: SupportedMimeType) => void
  /** Whether the button is disabled */
  disabled?: boolean
}

/**
 * Determines the MIME type from a file object.
 * Returns 'application/pdf' as default if type cannot be determined.
 */
const getMimeType = (file: File): SupportedMimeType => {
  const mimeType = file.mimetype?.toLowerCase()

  if (mimeType === 'application/pdf' || file.name?.toLowerCase().endsWith('.pdf')) {
    return 'application/pdf'
  }

  if (mimeType === 'image/png' || file.name?.toLowerCase().endsWith('.png')) {
    return 'image/png'
  }

  if (mimeType === 'image/jpeg' || file.name?.toLowerCase().match(/\.(jpg|jpeg)$/)) {
    return 'image/jpeg'
  }

  if (mimeType === 'image/gif' || file.name?.toLowerCase().endsWith('.gif')) {
    return 'image/gif'
  }

  if (mimeType === 'image/webp' || file.name?.toLowerCase().endsWith('.webp')) {
    return 'image/webp'
  }

  // Default to PDF for backwards compatibility
  return 'application/pdf'
}

/**
 * Custom trigger button with attachment icon.
 */
const AttachTriggerButton = ({ onClick, disabled, isBusy }: BusyButtonProps) => (
  <BusyButtonLink
    onClick={onClick}
    disabled={disabled}
    isBusy={isBusy}
    icon="paperclip"
    busyText=" "
    text=""
    title="Attach PDF or image"
  />
)

/**
 * AttachFileButton component provides a unified file upload button for the AI chat interface.
 * Supports PDF documents and images (PNG, JPG, JPEG, GIF, WebP).
 *
 * This component uses FileUploader with mode="simple" for direct file selection (no modal),
 * with Uppy handling the S3 upload in the background.
 *
 * @example
 * ```tsx
 * <AttachFileButton
 *   onFileAttached={(file, mimeType) => {
 *     setAttachmentUrl(file.uri)
 *     setAttachmentMimetype(mimeType)
 *   }}
 *   disabled={isLoading}
 * />
 * ```
 */
export const AttachFileButton = ({ onFileAttached, disabled }: AttachFileButtonProps) => {
  const handleUploadComplete: OnUploadCompleteFunction = useCallback(
    async (files: File[] = []) => {
      if (!isEmpty(files)) {
        const file = files[0]
        const mimeType = getMimeType(file)
        onFileAttached?.(file, mimeType)
      }
    },
    [onFileAttached]
  )

  const handleSetFileMeta = useCallback(() => {
    return {
      tags: ['chat-attachment'],
      description: 'File attached for AI analysis',
    }
  }, [])

  return (
    <FileUploader
      mode="simple"
      accept={ACCEPTED_FILE_TYPES}
      restrictions={FILE_RESTRICTIONS}
      onUploadComplete={handleUploadComplete}
      onSetFileMeta={handleSetFileMeta}
      disabled={disabled}
      trigger={AttachTriggerButton}
    />
  )
}
